import { useCallback, useMemo, useRef, useState } from 'react';

import Eye from '@crehana/compass.icons.fill/Eye';
import EyeOff from '@crehana/compass.icons.fill/EyeOff';
import Lock from '@crehana/compass.icons.fill/Lock';
import LockOutline from '@crehana/compass.icons.outline/Lock';
import Plus from '@crehana/compass.icons.outline/Plus';
import { LazyImage } from '@crehana/compass.ui';
import { cn } from '@crehana/compass.ui/utils';
import { formatDuration } from '@crehana/date';
import { useMediaQuery } from '@crehana/react-hooks';

import { StudioCourseConfigStateEnum } from '@/shared/types/graphql/globals.v5';

import {
  COURSE_CARD_IMAGE_PARAMS,
  DEFAULT_IMG_SKILLPATH,
} from '../../../constants';
import useTeamCoursesTranslation from '../../../hooks/useTeamCoursesTranslation';
import type { TCoursesTrack, TInformation } from '../../../types';
import { navigateToCourse } from '../../../utils/navigate-to-course';
import Information from './Information';
import InformationTablet from './InformationTablet';
import Option from './Option';

export type CourseCardRevampProps = {
  course: TCoursesTrack;
  blockingDependencies: TCoursesTrack[];
  hiddeOptions: boolean;
  onVisibleModalBlocking: () => void;
  onVisibleModalTrailer: () => void;
  isCourseTypeOwn: boolean;
  isNewCourse?: boolean;
  isRouteClosed: boolean;
};

const CourseCardRevamp: React.FC<CourseCardRevampProps> = ({
  course,
  blockingDependencies = [],
  hiddeOptions,
  onVisibleModalBlocking,
  onVisibleModalTrailer,
  isCourseTypeOwn,
  isNewCourse,
  isRouteClosed,
}) => {
  const { t } = useTeamCoursesTranslation();
  const { matches: isXs } = useMediaQuery({ only: 'xs' });
  const optionRef = useRef<HTMLButtonElement>(null);
  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  const durationCourse = useMemo(() => {
    if (course.duration && course.progress) {
      return t('LEARN_TEAM_TIME', {
        interpolation: {
          duration: formatDuration(
            course.duration - course.duration * (course.progress / 100),
          ),
        },
      });
    }
    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (course.duration)
      return t('LEARN_TEAM_TIME_DURATION', {
        interpolation: {
          duration: formatDuration(course.duration),
        },
      });

    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (course.duration && isXs) return formatDuration(course.duration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, isXs]);

  const { isComingSoon } = course;
  // rutas desahabilitada o cuando tenga dependencias por pre-requisitos
  const isHiddenCourse =
    course.visibilityState === StudioCourseConfigStateEnum.HIDDEN;
  const hasBlockingDependencies = blockingDependencies.length > 0;
  const shouldRenderTeamOptionButton: boolean =
    !hasBlockingDependencies &&
    !hiddeOptions &&
    !isHiddenCourse &&
    !isRouteClosed &&
    !isComingSoon;
  const allowedShowTrailer =
    isCourseTypeOwn &&
    !hiddeOptions &&
    !isHiddenCourse &&
    !isRouteClosed &&
    !isComingSoon;

  const handleRowActivate = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      // keyboard activation must only react to Enter/Space, matching native
      // button semantics — a bare onKeyDown would otherwise fire on every key
      if ('key' in event && event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
      if (isRouteClosed) return;

      // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
      if (isComingSoon) return;

      if (hasBlockingDependencies) {
        onVisibleModalBlocking?.();
        return;
      }

      // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
      if (isHiddenCourse || !course.lastVideoLectureUrl) return;

      navigateToCourse(course.lastVideoLectureUrl);
    },
    [
      hasBlockingDependencies,
      isHiddenCourse,
      isRouteClosed,
      isComingSoon,
      course.lastVideoLectureUrl,
      onVisibleModalBlocking,
    ],
  );

  const renderInformation = () => {
    const paramsInformation: TInformation = {
      title: course.courseName,
      duration: durationCourse,
      teacherName: course.profesorFullName,
      isRequired: Boolean(course.isRequired),
      visibilityState: course.visibilityState,
      isNewCourse,
      isComingSoon,
    };

    return isXs ? (
      <Information
        {...paramsInformation}
        isVisibleTeacherName={Boolean(course.progress && course.progress > 0)}
      />
    ) : (
      <InformationTablet {...paramsInformation} />
    );
  };

  const isRowBlocked = isRouteClosed || isComingSoon;
  const actionButtonClassName =
    'sm:tw-h-fit dark:tw-text-neutral-light-100 dark:tw-bg-neutral-dark-700 dark:tw-border-none tw-flex tw-items-center tw-justify-center tw-font-link1 tw-px-1.2 tw-py-0.8 tw-rounded-1 tw-border tw-border-neutral-light-500';
  const cursorClassName = isRowBlocked
    ? 'tw-cursor-not-allowed'
    : !isHiddenCourse && 'tw-cursor-pointer';

  // Main render
  return (
    <div
      className={cn(
        'tw-bg-neutral-light-100 tw-flex tw-flex-col sm:tw-flex-row tw-p-1.2 tw-relative tw-rounded-0.8 sm:tw-shadow-none sm:tw-p-0.8 tw-transition-all',
        'sm:dark:tw-bg-transparent dark:tw-bg-elevation-dark-04 dark:tw-shadow-none',
        'hover:tw-shadow-01 hover:tw-z-2',
        cursorClassName,
        isRowBlocked && 'tw-opacity-60',
      )}
      role="listitem"
    >
      <div
        className={cn(
          'tw-flex-1 tw-w-full tw-text-left tw-flex tw-flex-col sm:tw-flex-row',
          cursorClassName,
        )}
        role="button"
        tabIndex={isRowBlocked ? -1 : 0}
        aria-disabled={isRowBlocked}
        onClick={handleRowActivate}
        onKeyDown={handleRowActivate}
      >
        {/* seccion de imagen */}
        <div className="tw-relative tw-flex tw-flex-col tw-w-full tw-overflow-hidden tw-rounded-0.8 tw-h-8 tw-mb-0.8 sm:tw-mb-0 sm:tw-mr-1.2 sm:tw-w-[9.2rem] sm:tw-h-[5.2rem]">
          {allowedShowTrailer && (
            <button
              type="button"
              className="tw-flex tw-bg-[rgba(0,0,0,0.6)] hover:tw-opacity-100 tw-opacity-0 tw-absolute tw-w-full tw-h-full tw-inset-0 tw-items-center tw-justify-center tw-rounded-0.4 tw-z-7"
              onClick={event => {
                event.stopPropagation();
                onVisibleModalTrailer();
              }}
              onKeyDown={event => event.stopPropagation()}
            >
              <Eye className="tw-text-neutral-light-100" size={24} />
            </button>
          )}
          {isHiddenCourse ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-bg-neutral-light-300">
              <EyeOff size={24} className="tw-text-neutral-light-700" />
            </div>
          ) : (
            <LazyImage
              src={course.courseImage || DEFAULT_IMG_SKILLPATH}
              placeholder={DEFAULT_IMG_SKILLPATH}
              imgxParams={COURSE_CARD_IMAGE_PARAMS}
              alt={course.courseName || ''}
              isTransparent
              imgClassName="tw-object-cover"
              className="tw-w-full tw-h-full"
            />
          )}

          {hasBlockingDependencies && !isComingSoon && (
            <div
              data-testid="course-blocking-overlay"
              className="tw-bg-[#000000] tw-opacity-60 tw-absolute tw-w-full tw-h-full tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-0.4 tw-z-7"
            >
              <Lock className="tw-text-neutral-light-100" size={24} />
            </div>
          )}
        </div>
        {renderInformation()}

        {isComingSoon && (
          <button
            type="button"
            disabled
            aria-label={t('LEARN_TEAM_COURSE_COMING_SOON_TAG')}
            data-testid="course-coming-soon-lock"
            className={cn(
              actionButtonClassName,
              'tw-text-neutral-light-600 tw-border-dashed tw-cursor-not-allowed',
            )}
          >
            <LockOutline size={16} className="tw-mr-0.8 sm:tw-mr-0" />
            <span className="tw-block sm:tw-hidden">
              {t('LEARN_TEAM_COURSE_COMING_SOON_TAG')}
            </span>
          </button>
        )}

        {shouldRenderTeamOptionButton && (
          <button
            type="button"
            data-testid="course-options-button"
            className={cn(
              actionButtonClassName,
              'tw-text-neutral-light-800 tw-border-solid',
            )}
            onClick={event => {
              event.stopPropagation();
              setIsOpenDrawer(true);
            }}
            onKeyDown={event => event.stopPropagation()}
            ref={optionRef}
          >
            <Plus size={16} className="tw-mr-0.8 sm:tw-mr-0 tw-fill-current" />
            <span className="tw-block sm:tw-hidden">
              {t('LEARN_TEAM_OPTION')}
            </span>
          </button>
        )}
      </div>
      <div className="tw-relative tw-my-auto">
        <Option
          isOpen={isOpenDrawer}
          onOpenTrailer={onVisibleModalTrailer}
          setIsOpen={() => setIsOpenDrawer(false)}
          refPopper={optionRef}
          dataCourse={course}
        />
      </div>
    </div>
  );
};

export default CourseCardRevamp;
